import { useKeycloak } from "@react-keycloak/web";
import { Worker } from "@react-pdf-viewer/core";
import { App as AntApp, ConfigProvider } from "antd";
import { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "./app/store";
import CustomEmpty from "./components/ui/CustomEmpty";
import Loader from "./components/ui/Loader";
import { userLoggedIn } from "./features/auth/authSlice";
import SidebarLayout from "./layouts/SidebarLayout";
import AuthMiddleware from "./middleware/AuthMiddleware";
import { publicRouters, sidebarRouters } from "./routers/routers";
import { removeHash } from "./utils/hash";

const App: React.FC = () => {
  // const authLoading = useAuthChecked();
  // const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();
  const isAuth = keycloak?.authenticated && keycloak?.token;
  const [search] = useSearchParams();

  useEffect(() => {
    if (!!isAuth) {
      const userData = {
        access_token: keycloak?.token,
        user: {
          name: keycloak?.tokenParsed?.given_name,
          email: keycloak?.tokenParsed?.email,
        },
        role: "admin",
      };

      dispatch(userLoggedIn(userData));
      setLoading(false);
    } else {
      dispatch(userLoggedIn(null));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const type = search.get("type");
    const dynamicId = search.get("dynamic-id");
    const dynamicKeyword = search.get("dynamic-keyword");

    if (type) {
      localStorage.setItem("product-type", JSON.stringify(type));
    }

    if (dynamicId && dynamicKeyword) {
      localStorage.setItem(
        "dynamic",
        JSON.stringify({
          dynamicId,
          dynamicKeyword,
        })
      );
    }
  }, [search]);

  const dynamic = localStorage.getItem("dynamic");
  const dynamicData = dynamic ? JSON.parse(dynamic) : null;

  console.log("dynamicId", removeHash(dynamicData?.dynamicId));
  console.log("dynamicKeyword", removeHash(dynamicData?.dynamicKeyword));

  if (loading) {
    return <Loader />;
  }

  return (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#147aff",
            colorPrimaryText: "#3d4a5c",
            fontFamily: "Inter",
            colorBorder: "#5e718d1f",
          },
          components: {
            Button: {
              colorPrimary: "#0d0d0d",
              colorPrimaryHover: "#222",
            },
            Timeline: {
              tailColor: "#e82c4c",
              itemPaddingBottom: 18,
            },
            Checkbox: {
              colorBorder: "#afbaca",
              colorPrimary: "#e82c4c",
              colorPrimaryHover: "#e82c4c",
            },
            DatePicker: {
              colorTextPlaceholder: "#ff4d4f",
            },
            Select: {
              colorBgContainer: "#242C38",
              colorBorder: "#5e718d1f",
              colorTextPlaceholder: "#5e718d",
              colorIcon: "red",
              colorText: "#e9eff6",
            },
            Table: {
              colorBgContainer: "#242c38",
              colorText: "#E9EFF6",
              colorTextHeading: "#8897AE",
            },
            Pagination: {
              colorBgContainer: "#E9EFF6",
            },
            Input: {
              // borderRadiusLG: 6,
              controlHeightLG: 46,
            },
          },
        }}
        renderEmpty={() => <CustomEmpty rootClassName="py-20" />}
      >
        <AntApp>
          <Routes>
            <Route
              path="/"
              element={
                <Navigate to={isAuth ? "/admin/dashboard" : "/admin/login"} />
              }
            />

            {/* Public routers */}
            {publicRouters.map(({ path, element: Element }, idx) => (
              <Route
                path={path}
                key={idx + path}
                element={
                  <Suspense fallback={<Loader />}>
                    <Element />
                  </Suspense>
                }
              />
            ))}

            {/* sidebar routers */}
            {sidebarRouters.map(({ path, element: Element, roles }, idx) => (
              <Route path={path} element={<SidebarLayout />} key={idx + path}>
                <Route
                  path={path}
                  element={
                    <AuthMiddleware roles={roles}>
                      <Suspense fallback={<Loader />}>
                        <Element />
                      </Suspense>
                    </AuthMiddleware>
                  }
                />
              </Route>
            ))}
          </Routes>
        </AntApp>
      </ConfigProvider>
    </Worker>
  );
};

export default App;
